import { Card } from "@/components/ui/card";
import { CheckCircle2, XCircle, AlertCircle } from "lucide-react";
import { Badge } from "@/components/ui/badge";

interface ParsedSection {
  section: string;
  extracted: boolean;
  content: string;
  issues?: string[];
}

interface ATSParsingPreviewProps {
  parsingData: {
    raw_text: string;
    sections: ParsedSection[];
  };
}

export const ATSParsingPreview = ({ parsingData }: ATSParsingPreviewProps) => {
  const getStatusIcon = (extracted: boolean, hasIssues: boolean) => {
    if (extracted && !hasIssues) {
      return <CheckCircle2 className="h-5 w-5 text-accent" />;
    }
    if (hasIssues) {
      return <AlertCircle className="h-5 w-5 text-yellow-500" />;
    }
    return <XCircle className="h-5 w-5 text-destructive" />;
  };

  const getStatusColor = (extracted: boolean, hasIssues: boolean) => {
    if (extracted && !hasIssues) return "bg-accent/5 border-accent/30";
    if (hasIssues) return "bg-yellow-500/5 border-yellow-500/30";
    return "bg-destructive/5 border-destructive/30";
  };

  return (
    <div className="space-y-6">
      <Card className="p-6">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="h-6 w-6 text-primary" />
          <h3 className="text-xl font-semibold">ATS Parser View</h3>
        </div>
        <p className="text-muted-foreground mb-6">
          This is exactly what an ATS system sees when it parses your resume. Green sections were extracted successfully, yellow sections have issues, and red sections were missed or misparsed.
        </p>

        {/* Legend */}
        <div className="flex flex-wrap gap-4 mb-6 p-4 bg-card/50 rounded-lg border border-border">
          <div className="flex items-center gap-2">
            <CheckCircle2 className="h-4 w-4 text-accent" />
            <span className="text-sm text-foreground">Successfully extracted</span>
          </div>
          <div className="flex items-center gap-2">
            <AlertCircle className="h-4 w-4 text-yellow-500" />
            <span className="text-sm text-foreground">Extracted with issues</span>
          </div>
          <div className="flex items-center gap-2">
            <XCircle className="h-4 w-4 text-destructive" />
            <span className="text-sm text-foreground">Not extracted / Misparsed</span>
          </div>
        </div>

        {/* Parsed Sections */}
        <div className="space-y-4">
          {parsingData.sections.map((section, index) => (
            <div
              key={index}
              className={`p-4 rounded-lg border-2 ${getStatusColor(section.extracted, !!section.issues?.length)}`}
            >
              <div className="flex items-start justify-between mb-2">
                <div className="flex items-center gap-2">
                  {getStatusIcon(section.extracted, !!section.issues?.length)}
                  <h4 className="font-semibold text-lg text-foreground">{section.section}</h4>
                  {section.extracted ? (
                    <Badge variant="outline" className="bg-background">
                      Extracted
                    </Badge>
                  ) : (
                    <Badge variant="destructive">Not Found</Badge>
                  )}
                </div>
              </div>

              {/* Section Content */}
              <div className="mt-3 p-3 bg-background rounded border border-border">
                <pre className="text-sm whitespace-pre-wrap font-mono text-foreground">
                  {section.content || "(No content extracted)"}
                </pre>
              </div>

              {/* Issues */}
              {section.issues && section.issues.length > 0 && (
                <div className="mt-3 space-y-2">
                  <p className="text-sm font-semibold text-yellow-500">
                    Parsing Issues:
                  </p>
                  <ul className="list-disc list-inside space-y-1">
                    {section.issues.map((issue, issueIndex) => (
                      <li key={issueIndex} className="text-sm text-muted-foreground">
                        {issue}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ))}
        </div>
      </Card>

      {/* Raw Text Preview */}
      <Card className="p-6">
        <h3 className="text-xl font-semibold mb-4 text-foreground">Raw Extracted Text</h3>
        <p className="text-sm text-muted-foreground mb-4">
          This is the raw text that an ATS extracts from your resume before parsing it into sections.
        </p>
        <div className="p-4 bg-card rounded-lg border border-border max-h-96 overflow-y-auto">
          <pre className="text-xs whitespace-pre-wrap font-mono text-foreground">
            {parsingData.raw_text}
          </pre>
        </div>
      </Card>
    </div>
  );
};